import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonInput from "../../../components/input/CommonInput";
import CommonButton from "../../../components/button/CommonButton";
import PhoneInput from "../../../components/input/PhoneInput";
import DocumentUpload from "../../../components/input/DocumentUpload";
import HomeHeader from "../../../components/header/HomeHeader";
import {
  emailValidator,
  passwordValidator,
  handleBlanckSpaceInput,
  handleUpperCaseNoSpace,
  getErrorMessage
} from "../../../constants/globalFunction";
import { imagePath } from "../../../constants/imagePath";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { FaUser, FaEnvelope, FaLock } from "react-icons/fa";
import "./SignUp.css";
import { appApiHandler } from "../../../api/appApiHandler";
import {
  saveToken,
  saveUserId,
  saveUserType,
} from "../../../constants/asyncStoreManager";
import { useDispatch } from "react-redux";
import { setUserData } from "../../../store/slices/userState";
import { setIsLoggedIn } from "../../../store/slices/authState";
import { string } from "../../../constants/string";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userType, setUserType] = useState("retailer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [country, setCountry] = useState('India');
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [phonenoError, setPhonenoError] = useState("");
  const [loading, setLoading] = useState(false);

  const [gst, setGst] = useState(null);
  const [gstNo, setGstNo] = useState("");
  const [gstNoError, setGstNoError] = useState("");
  const [gstError, setGstError] = useState("");
  const [panCard, setPanCard] = useState(null);
  const [panCardNo, setPanCardNo] = useState("");
  const [panCardNoError, setPanCardNoError] = useState("");
  const [panCardError, setPanCardError] = useState("");
  const [passport, setPassport] = useState(null);
  const [passportNo, setPassportNo] = useState("");
  const [passportNoError, setPassportNoError] = useState("");
  const [passportError, setPassportError] = useState("");
  const [profileImage, setProfileImage] = useState(null);

  const validateValues = () => {
    let isValid = true;

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter phone number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (countryCode === "+91") {
      const digitsOnlyLength = phoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (!email.trim()) {
      setEmailError("Please enter email");
      isValid = false;
    } else if (!emailValidator(email)) {
      setEmailError("Please enter a valid email");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!password.trim()) {
      setPasswordError("Please enter password");
      isValid = false;
    } else if (!passwordValidator(password)) {
      setPasswordError(
        "Password must be at least 8 characters and include one uppercase letter, one number, and one special character"
      );
      isValid = false;
    } else {
      setPasswordError("");
    }

    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Please enter confirm password");
      isValid = false;
    } else if (confirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match");
      isValid = false;
    } else {
      setConfirmPasswordError("");
    }

    if (userType === "wholeseller" && countryCode === "+91") {
      if (!gstNo.trim()) {
        setGstNoError("Please enter GST number");
        isValid = false;
      }
      if (!gst) {
        setGstError("Please upload GST document");
        isValid = false;
      }
      if (!panCardNo.trim()) {
        setPanCardNoError("Please enter PAN card number");
        isValid = false;
      }
      if (!panCard) {
        setPanCardError("Please upload PAN card document");
        isValid = false;
      }
    }

    if (userType === "wholeseller" && countryCode !== "+91") {
      if (!passportNo.trim()) {
        setPassportNoError("Please enter passport number");
        isValid = false;
      }
      if (!passport) {
        setPassportError("Please upload passport document");
        isValid = false;
      }
    }

    return isValid;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateValues();
    if (isValid) {
      try {
        const reqData = new FormData();
        reqData.append("user_type", userType);
        reqData.append("name", name.trim());
        reqData.append("email", email.trim());
        reqData.append("password", password.trim());
        reqData.append("phoneno", phoneno.trim());
        reqData.append("country_code", countryCode.trim());
        reqData.append("country", country.trim());

        if (profileImage) {
          reqData.append("profile_image", profileImage);
        }

        console.log("coute", country)

        if (userType === "wholeseller" && countryCode === "+91") {
          if (gst) {
            reqData.append("gst", gst);
          }
          reqData.append("gst_no", gstNo.trim());

          if (panCard) {
            reqData.append("pancard", panCard);
          }
          reqData.append("pancard_no", panCardNo.trim());
        } else if (userType === "wholeseller" && countryCode !== "+91") {
          if (passport) {
            reqData.append("passport", passport);
          }
          reqData.append("passport_no", passportNo.trim());
        }

        setLoading(true);
        const response = await appApiHandler.register(reqData);

        if (response?.status === 200) {
          await saveUserId(response?.data?._id);
          await saveUserType(response?.data?.user_type);
          await saveToken(response?.token);
          dispatch(setUserData(response?.data));
          dispatch(setIsLoggedIn(true));

          showSuccessToast("Login successful.");
          navigate("/");
        } else if (response?.message) {
          showErrorToast(getErrorMessage(response?.message));
        } else {
          showErrorToast(string.error.failed_due_to_internal_error);
        }
      } catch (err) {
        showErrorToast(err.message || "Signup failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const onPressLogin = () => {
    navigate("/login");
  };

  const onPressWholeseller = () => {
    setGst(null);
    setGstError("");
    setUserType("wholeseller");
  };

  const onChangeCountryCode = (country) => {
    setCountryCode(country.dial_code);
    setCountry(country.name);
    if (country.dial_code === "+91") {
      setPassport(null);
      setPassportError("");
    } else {
      setGst(null);
      setGstError("");
      setPanCard(null);
      setPanCardError("");
    }
  };

  const handleFileUpload = (setFile, setError) => (file) => {
    if (file) {
      setFile(file);
      setError("");
    }
  };

  return (
    <div className="signup-page" style={{ backgroundImage: `url(${imagePath.background})` }}>
      <HomeHeader />
      <div className="signup-content">
        <div className="signup-card">
        <img
          src={imagePath.appLogo}
          alt="Codeinfoway"
          className="signup-logo"
        />
        <h1 className="signup-title">Create New Account</h1>

        {/* User Type Toggle */}
        <div className="signup-toggle-container">
          <button
            className={`signup-toggle-button ${userType === "retailer"
              ? "signup-toggle-button-selected-left"
              : ""
              }`}
            onClick={() => setUserType("retailer")}
            type="button"
          >
            <span
              className={`signup-toggle-button-text ${userType === "retailer"
                ? "signup-toggle-button-text-selected"
                : ""
                }`}
            >
              Retailer
            </span>
          </button>
          <button
            className={`signup-toggle-button ${userType === "wholeseller"
              ? "signup-toggle-button-selected-right"
              : ""
              }`}
            onClick={onPressWholeseller}
            type="button"
          >
            <span
              className={`signup-toggle-button-text ${userType === "wholeseller"
                ? "signup-toggle-button-text-selected"
                : ""
                }`}
            >
              Wholesaler
            </span>
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <CommonInput
            label="Name"
            value={name}
            onChange={setName}
            placeholder="Enter name"
            error={nameError}
            leftIcon={<FaUser className="signup-icon" />}
          />

          <CommonInput
            label="Email"
            value={email}
            onChange={setEmail}
            placeholder="Enter email"
            error={emailError}
            type="email"
            leftIcon={<FaEnvelope className="signup-icon" />}
          />

          <PhoneInput
            label="Phone number"
            code={countryCode}
            onChangeCode={onChangeCountryCode}
            onChangeText={setPhoneno}
            value={phoneno}
            placeholder="Enter phone number"
            error={phonenoError}
            editable={!loading}
          />

          <CommonInput
            label="Password"
            value={password}
            onChange={(value) => setPassword(handleBlanckSpaceInput(value))}
            placeholder="Enter password"
            error={passwordError}
            secureTextEntry
            leftIcon={<FaLock className="signup-icon" />}
          />

          <CommonInput
            label="Confirm Password"
            value={confirmPassword}
            onChange={(value) =>
              setConfirmPassword(handleBlanckSpaceInput(value))
            }
            placeholder="Enter confirm password"
            error={confirmPasswordError}
            secureTextEntry
            leftIcon={<FaLock className="signup-icon" />}
          />

          <DocumentUpload
            label="Profile Image"
            required={false}
            value={profileImage}
            onChange={(file) =>{
              setProfileImage(file)
            }}
            disabled={loading}
            placeholder="Select Profile Image"
          />

          {/* Wholeseller specific fields for India */}
          {userType === "wholeseller" && countryCode === "+91" && (
            <>
              <CommonInput
                label="GST Number"
                value={gstNo}
                onChange={(value) => setGstNo(handleUpperCaseNoSpace(value))}
                placeholder="Enter GST number"
                error={gstNoError}
              />

              <DocumentUpload
                label="GST"
                required={true}
                value={gst}
                onChange={handleFileUpload(setGst, setGstError)}
                error={gstError}
                disabled={loading}
              />

              <CommonInput
                label="PAN Card Number"
                value={panCardNo}
                onChange={(value) =>
                  setPanCardNo(handleUpperCaseNoSpace(value))
                }
                placeholder="Enter PAN card number"
                error={panCardNoError}
              />

              <DocumentUpload
                label="PAN Card"
                required={true}
                value={panCard}
                onChange={handleFileUpload(setPanCard, setPanCardError)}
                error={panCardError}
                disabled={loading}
              />
            </>
          )}

          {/* Wholeseller specific fields for non-India */}
          {userType === "wholeseller" && countryCode !== "+91" && (
            <>
              <CommonInput
                label="Passport Number"
                value={passportNo}
                onChange={(value) =>
                  setPassportNo(handleUpperCaseNoSpace(value))
                }
                placeholder="Enter passport number"
                error={passportNoError}
              />

              <DocumentUpload
                label="Passport"
                required={true}
                value={passport}
                onChange={handleFileUpload(setPassport, setPassportError)}
                error={passportError}
                disabled={loading}
              />
            </>
          )}

          <CommonButton
            title={loading ? "Creating Account..." : "Sign Up"}
            type="submit"
            disabled={loading}
          />
        </form>

        <div className="signup-bottom-text">
          Already have an account?{" "}
          <span className="signup-login-text" onClick={onPressLogin}>
            Log In
          </span>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
